import { Card, Dropdown, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { SettingActionRow } from '../../../components/setting-action-row';
import { SettingToggleRow } from '../../../components/setting-toggle-row';
import { CART_SEARCH_TYPES } from '../../../constants/cart';
import { useUser } from '../../../context/page-context';
import type { Settings } from '../../../context/settings-context';
import { useSettings, useUpdateSettings } from '../../../context/settings-context';
import { DOCS_URLS, I18N_PAGE } from '../constants';
import { useSettingToggle } from '../hooks/use-setting-toggle';
import type { ConfigurationsModal } from '../types';
import { PurchaseIdAffixesRow } from './purchase-id-affixes-row';
import { SettingRow } from './setting-row';

const FIELDS = `${I18N_PAGE}.cards.other_configurations.fields` as const;
const TITLE = `${I18N_PAGE}.cards.other_configurations.title` as const;
const COMPONENTS = 'features:configurations.components';

export function OtherConfigurationsCard({
  openVueModal,
}: {
  openVueModal: (modal: ConfigurationsModal) => void;
}) {
  const { isSparkStaff } = useUser();
  const settings = useSettings();
  const { mutate } = useUpdateSettings();

  const applyPriceBreaks = useSettingToggle('applyPriceBreaksAcrossVariants', true);
  const hideAICartFeatures = useSettingToggle('hideAICartFeatures', false);
  const searchType = settings.cartSearchType ?? 'full';

  const searchOptions = CART_SEARCH_TYPES.map((value) => ({
    value,
    label: t(`${FIELDS}.cart_search_type.options.${value}`),
  }));

  return (
    <Card>
      <Card.Header headingLevel="h2">{t(TITLE)}</Card.Header>
      <Card.Body>
        <div className="divide-y divide-gray-200">
          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.apply_price_breaks_across_variants.label`)}
              description={t(`${FIELDS}.apply_price_breaks_across_variants.description`)}
              checked={applyPriceBreaks.checked}
              onChange={applyPriceBreaks.onChange}
              learnMoreUrl={DOCS_URLS.applyPriceBreaksAcrossVariants}
            />
          </SettingRow>

          <SettingRow>
            <PurchaseIdAffixesRow />
          </SettingRow>

          <SettingRow>
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 flex-col">
                <Text>{t(`${FIELDS}.cart_search_type.label`)}</Text>
                <Text size="sm" variant="secondary">
                  {t(`${FIELDS}.cart_search_type.description`)}{' '}
                  <LearnMoreLink link={DOCS_URLS.cartSearchType} />
                </Text>
              </div>
              <div className="w-44 shrink-0">
                <Dropdown
                  value={searchType}
                  options={searchOptions}
                  onChange={(value) =>
                    mutate({ key: 'cartSearchType', value: value as Settings['cartSearchType'] })
                  }
                />
              </div>
            </div>
          </SettingRow>

          {isSparkStaff && (
            <SettingRow>
              <SettingActionRow
                label={t(`${FIELDS}.custom_email_domain.label`)}
                description={t(`${FIELDS}.custom_email_domain.description`)}
                learnMoreUrl={DOCS_URLS.customEmailDomain}
                actionLabel={t(`${COMPONENTS}.setting_action_row.edit`)}
                onAction={() => openVueModal('custom-email-domain')}
              />
            </SettingRow>
          )}

          <SettingRow>
            <SettingToggleRow
              label={t(`${FIELDS}.hide_ai_cart_features.label`)}
              description={t(`${FIELDS}.hide_ai_cart_features.description`)}
              checked={hideAICartFeatures.checked}
              onChange={hideAICartFeatures.onChange}
              learnMoreUrl={DOCS_URLS.hideAICartFeatures}
            />
          </SettingRow>
        </div>
      </Card.Body>
    </Card>
  );
}
